/* eslint-disable @typescript-eslint/no-empty-function */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { Dispatch, PropsWithChildren, ReactNode, SetStateAction } from "react";
import type { ContactMetier, Poste } from "models";
import { usePersistentValue } from "@sdeapps/react-core";
import { localStorageKeys } from "config/app-config";
import type { ActionType } from "./enums/ActionType";
import { ListDensity } from "./enums/ListDensity";

export type ContactMetierActionCallback = (
  targetContactMetier: ContactMetier
) => Promise<void> | void;

export type ContactMetierMultipleEditCallback = (
  contactsMetiersIdsToUpdate: Array<string>,
  nouveauPoste: Poste
) => void;

export interface IContactMetierActionsData {
  openActionDialog: (actionType: ActionType, contactMetierToEdit?: ContactMetier) => void;
  closeActionDialog: () => void;
  targetContactMetier?: ContactMetier;
  isActionDialogOpen: boolean;
  actionType?: ActionType;
  onDelete?: ContactMetierActionCallback;
  onEdit?: ContactMetierActionCallback;
  onMultipleEdit?: ContactMetierMultipleEditCallback;

  selectedContactMetiers: Array<ContactMetier>;
  deselectAll: () => void;
  selectOnlyThese: (cms: Array<ContactMetier>) => void;
  toggleUniqueSelection: (cm: ContactMetier) => void;

  density: ListDensity;
  setDensity: Dispatch<SetStateAction<ListDensity>>;
}

const defaultContextData: IContactMetierActionsData = {
  openActionDialog: () => {},
  closeActionDialog: () => {},
  isActionDialogOpen: false,

  selectedContactMetiers: [],
  deselectAll: () => {},
  selectOnlyThese: () => {},
  toggleUniqueSelection: () => {},

  density: ListDensity.DENSE,
  setDensity: () => {},
};
const ContactMetierActionsContext = createContext(defaultContextData);

interface ContactMetierActionsProviderProps extends PropsWithChildren {
  onDelete?: ContactMetierActionCallback;
  onEdit?: ContactMetierActionCallback;
  onMultipleEdit?: (
    contactsMetiersIdsToUpdate: Array<string>,
    nouveauPoste: Poste
  ) => Promise<void>;
  onListDensityChange?: () => Promise<void>;
}

export function ContactMetierActionsProvider({
  children,
  onDelete,
  onEdit,
  onMultipleEdit,
  onListDensityChange,
}: Readonly<ContactMetierActionsProviderProps>): ReactNode {
  const [targetContactMetier, setTargetContactMetier] = useState<ContactMetier>();
  const [isActionDialogOpen, setIsActionDialogOpen] = useState<boolean>(false);
  const [actionType, setActionType] = useState<ActionType>();
  const [selectedContactMetiers, setSelectedContactMetiers] = useState<Array<ContactMetier>>([]);
  const [density, setDensity] = usePersistentValue<ListDensity>(
    localStorageKeys.listDensity,
    ListDensity.CONFORTABLE
  );

  useEffect(() => {
    if (onListDensityChange != null) {
      void onListDensityChange();
    }
  }, [density, onListDensityChange]);

  const deselectAll = useCallback((): void => {
    setSelectedContactMetiers([]);
  }, []);

  const selectOnlyThese = useCallback((cms: Array<ContactMetier>): void => {
    setSelectedContactMetiers([...cms]);
  }, []);

  const toggleUniqueSelection = useCallback(
    (contactMetier: ContactMetier): void => {
      if (selectedContactMetiers.some((cm) => cm.id === contactMetier.id)) {
        setSelectedContactMetiers(
          selectedContactMetiers.filter((cm) => cm.id !== contactMetier.id)
        );
      } else {
        setSelectedContactMetiers([...selectedContactMetiers, contactMetier]);
      }
    },
    [selectedContactMetiers]
  );

  const _onDelete = useCallback(
    async (targetContactMetier: ContactMetier): Promise<void> => {
      deselectAll();
      await onDelete?.(targetContactMetier);
    },
    [deselectAll, onDelete]
  );

  const _onMultipleEdit = useCallback(
    async (contactsMetiersIdsToUpdate: Array<string>, nouveauPoste: Poste): Promise<void> => {
      deselectAll();
      await onMultipleEdit?.(contactsMetiersIdsToUpdate, nouveauPoste);
    },
    [deselectAll, onMultipleEdit]
  );

  const data: IContactMetierActionsData = useMemo(
    () => ({
      openActionDialog: (actionType: ActionType, targetContactMetier?: ContactMetier) => {
        setTargetContactMetier(targetContactMetier);
        setActionType(actionType);
        setIsActionDialogOpen(true);
      },
      closeActionDialog: () => {
        setIsActionDialogOpen(false);
      },
      targetContactMetier,
      isActionDialogOpen,
      actionType,

      onDelete: _onDelete,
      onEdit,
      onMultipleEdit: _onMultipleEdit,

      selectedContactMetiers,
      deselectAll,
      selectOnlyThese,
      toggleUniqueSelection,

      density,
      setDensity,
    }),
    [
      targetContactMetier,
      isActionDialogOpen,
      actionType,
      _onDelete,
      onEdit,
      _onMultipleEdit,
      selectedContactMetiers,
      deselectAll,
      selectOnlyThese,
      toggleUniqueSelection,
      density,
      setDensity,
    ]
  );

  return (
    <ContactMetierActionsContext.Provider value={data}>
      {children}
    </ContactMetierActionsContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useContactMetierActions(): IContactMetierActionsData {
  const context = useContext(ContactMetierActionsContext);

  if (context == null) {
    throw new Error("useContactMetierActions must be used within a ContactMetierActionsProvider");
  }

  return context;
}
